import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import WebGalleryPhotoGroupContainer from '../../../WebGalleryPhotoGroup/WebGalleryPhotoGroupContainer';
import WebGalleryAlbumNullState from '../../../WebGalleryAlbumNullState';
import WebGalleryActionBar from '../../../WebGalleryActionBar';
import { AlbumSlideshowForModeCheck } from '../../../../../../graphql/queries/albums';
import { ALBUM_SLIDESHOW_STATUS } from '../../../../../../constants/albums';
import ErrorView from '../../../../../../components/global/ErrorView';
import { withContributorPermissions } from '../../../../../pro/services/contributors';
import { AlbumForWebGalleriesPhotos } from '../../../../graphql/queries/album';
import SpecialtyAlbumGreetingDismissiblePersistent from '../../../../../pro/views/AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal/dismissiblePersistent';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../../../../../../constants/contributors';
import WebGalleryAlbumMeta from '../../../../containers/WebGalleryAlbumMeta';

const propTypes = {
  loading: PropTypes.bool,
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    is_web_gallery: PropTypes.bool,
    is_private_web_gallery: PropTypes.bool,
    root_album_folder: PropTypes.shape({
      id: PropTypes.string,
      join_code: PropTypes.string,
    }),
    creator: PropTypes.shape({
      slug: PropTypes.string,
    }),
    display_public_photo_attribution: PropTypes.bool.isRequired,
  }),
  photos: PropTypes.shape({
    total: PropTypes.number,
    count: PropTypes.number,
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        thumbnail_urls: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        ),
        watermarked_urls: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        ),
        is_favorite: PropTypes.bool,
      }),
    ),
  }),
  loadMorePhotos: PropTypes.func.isRequired,
};

const WebGalleryMemoryBookAlbum = ({
  album,
  loading,
  authenticated,
  loadMorePhotos,
  photos,
  slideshow,
  isOwner,
  isContributor,
}) => {
  if (loading || !album || !slideshow) {
    return (
      <div className="d-block text-center mt-6">
        <span className="spinner" />
      </div>
    );
  }

  if (
    !authenticated &&
    !isContributor &&
    slideshow?.status === ALBUM_SLIDESHOW_STATUS.DRAFT
  ) {
    return (
      <ErrorView
        location={{
          state: {
            error: 'This Memories Slideshow has not been published yet.',
          },
        }}
      />
    );
  }

  if (
    !(isOwner && authenticated) &&
    slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED
  ) {
    return <Redirect to={`/albums/${album.id}/slideshow`} />;
  }

  const isNullState = (photos?.total ?? 0) === 0;

  return (
    <Fragment>
      <WebGalleryAlbumMeta album={album} />
      <WebGalleryActionBar album={album} />

      <div className="px-sm-2 px-md-5 px-0">
        {isNullState && <WebGalleryAlbumNullState album={album} />}
      </div>

      <WebGalleryPhotoGroupContainer
        album={album}
        photoGroup={{
          id: null,
        }}
        photos={photos}
        loadMore={loadMorePhotos}
        renderMeta={false}
      />
      <SpecialtyAlbumGreetingDismissiblePersistent
        albumId={album.id}
        onAddPhotos={() => {
          window.location.assign(album?.auto_contributor_share_url);
        }}
        enabled={
          !!album?.auto_contributor_share_url &&
          album.auto_contributor_settings.upload_access !==
            CONTRIBUTOR_UPLOAD_ACCESS.NONE
        }
      />
    </Fragment>
  );
};

WebGalleryMemoryBookAlbum.propTypes = propTypes;

const withAlbumPhotos = graphql(AlbumForWebGalleriesPhotos, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
      ignore_watermark: true,
      limit: 100,
      offset: 0,
    },
  }),
  props: ({ data: { album, fetchMore } }) => ({
    photos: album?.photos_ungrouped,
    loadMorePhotos() {
      return fetchMore({
        variables: {
          offset: album.photos_ungrouped.records.length,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          return {
            ...previousResult,
            album: {
              ...previousResult.album,
              photos_ungrouped: {
                ...previousResult.album.photos_ungrouped,
                records: [
                  ...previousResult.album.photos_ungrouped.records,
                  ...fetchMoreResult.album.photos_ungrouped.records,
                ],
              },
            },
          };
        },
      });
    },
  }),
});

const withAlbumWithSlideshow = graphql(AlbumSlideshowForModeCheck, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album } }) => ({
    slideshow: album?.slideshow,
  }),
  skip: ({ match }) => !match?.params?.albumId,
});

export default compose(
  withRouter,
  withContributorPermissions(({ match }) => ({
    entityId: match?.params?.albumId,
  })),
  withAlbumPhotos,
  withAlbumWithSlideshow,
)(WebGalleryMemoryBookAlbum);
